import Axios from "axios";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { api_url } from "../../../config";
import useStore from "../../../context/useStore";
import tools from "../../../helpers/tools";
import { EmptyState, Pill } from "../../common/DataList";
import { FactList, FicheHeader, copyToClipboard } from "../../common/Fiche";
import { buildRecipientEyebrow, buildRecipientFacts, buildRecipientPills, getOrientationSummary } from "./recipientFacts";

// ============================================================
// Aperçu d'une fiche allocataire (« coup d'œil »), pour un panneau latéral :
// l'en-tête d'identité de la fiche — sur-titre, nom, pastilles, faits
// copiables, orientation — et le dernier contrat. Deux lectures seulement
// (la fiche et les organismes d'orientation), en Axios direct : une erreur
// s'affiche dans le panneau, sans toast. La fiche complète reste à un clic.
// ============================================================

let partenaires = null;
// Organismes d'orientation : lus une fois par session d'onglet.
function chargerPartenaires() {
  if (!partenaires) {
    partenaires = Axios.get(api_url + "organizations/orientation_partners")
      .then((reponse) => (Array.isArray(reponse.data) ? reponse.data : []))
      .catch(() => {
        partenaires = null;
        return [];
      });
  }
  return partenaires;
}

// Dernier contrat de la fiche : type, période, en cours ou échu.
export function resumeDernierContrat(fiche = {}, constants = {}, maintenant = DateTime.local()) {
  const contrats = (fiche.contracts || []).filter((c) => c && c.beginAt);
  const contrat = contrats[contrats.length - 1];
  if (!contrat) return null;
  const type = tools.findIn(constants.CONTRACT_TYPES || [], "id", contrat.type).name || contrat.type || "Contrat";
  const debut = DateTime.fromISO(contrat.beginAt);
  const fin = contrat.endAt ? DateTime.fromISO(contrat.endAt) : null;
  const periode = fin ? `du ${debut.toFormat("dd/MM/yyyy")} au ${fin.toFormat("dd/MM/yyyy")}` : `depuis le ${debut.toFormat("dd/MM/yyyy")}`;
  const enCours = debut <= maintenant && (!fin || fin >= maintenant);
  return { libelle: `${type} ${periode}`, enCours };
}

const SqueletteFaits = () => (
  <div className="apercu-allocataire__squelette" aria-hidden="true">
    {[70, 45, 60, 38, 52, 64].map((largeur, index) => (
      <span key={index} className="skeleton-bar" style={{ width: `${largeur}%` }} />
    ))}
  </div>
);

const ApercuAllocataire = ({ allocataireId, suggestion = {} }) => {
  const [state] = useStore();
  const constants = Array.isArray(state.constants.items) ? {} : state.constants.items || {};
  const [fiche, setFiche] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [erreur, setErreur] = useState(null);
  const [tentative, setTentative] = useState(0);

  useEffect(() => {
    let actif = true;
    setFiche(null);
    setErreur(null);
    Promise.all([Axios.get(api_url + `recipients/${allocataireId}`), chargerPartenaires()])
      .then(([reponse, organismes]) => {
        if (!actif) return;
        setOrgs(organismes);
        setFiche(reponse.data || {});
      })
      .catch((e) => {
        if (!actif) return;
        const statut = e && e.response ? e.response.status : null;
        setErreur(statut === 403 || statut === 404 ? "inaccessible" : "indisponible");
      });
    return () => {
      actif = false;
    };
  }, [allocataireId, tentative]);

  const titreProvisoire = [String(suggestion.nom || "").toUpperCase(), suggestion.prenom].filter(Boolean).join(" ");

  if (erreur) {
    return (
      <div className="list-card apercu-allocataire__erreur" role="alert">
        {erreur === "inaccessible" ? (
          <EmptyState
            icon="fa-lock"
            title="Cette fiche n'est pas accessible"
            hint="Elle a pu être archivée, anonymisée, ou n'être pas suivie par votre organisme."
          />
        ) : (
          <EmptyState
            icon="fa-exclamation-triangle"
            title="La fiche n'a pas pu être chargée"
            hint="Vérifiez votre connexion puis réessayez."
            action={{ label: "Réessayer", onClick: () => setTentative((n) => n + 1) }}
          />
        )}
      </div>
    );
  }

  if (!fiche) {
    return (
      <header className="fiche-header list-card" aria-busy="true">
        <div className="fiche-header__eyebrow">Allocataire</div>
        <h2 className="fiche-header__title">{titreProvisoire || "Chargement de la fiche…"}</h2>
        <SqueletteFaits />
        <span className="sr-only">Chargement de la fiche…</span>
      </header>
    );
  }

  const orientation = getOrientationSummary(fiche, orgs);
  const contrat = resumeDernierContrat(fiche, constants);
  const faits = [
    ...buildRecipientFacts(fiche, { constants }),
    ...(contrat
      ? [
          {
            id: "contrat",
            label: "Dernier contrat",
            value: contrat.libelle,
            render: () => (
              <span className="fact__value">
                {contrat.libelle}{" "}
                <Pill tone={contrat.enCours ? "ok" : "muted"}>{contrat.enCours ? "en cours" : "échu"}</Pill>
              </span>
            ),
          },
        ]
      : []),
  ];

  const copier = (valeur) =>
    copyToClipboard(valeur).then(
      () => toast.success("La valeur a été copiée dans votre presse-papier"),
      () => toast.error("Impossible de copier la valeur")
    );

  return (
    <FicheHeader
      headingLevel="h2"
      eyebrow={buildRecipientEyebrow(fiche, { constants })}
      title={tools.getFullname(fiche) || titreProvisoire || "Allocataire"}
      pills={buildRecipientPills(fiche).map((p) => (
        <Pill key={p.id} tone={p.tone} icon={p.icon}>
          {p.label}
        </Pill>
      ))}
    >
      <FactList facts={faits} onCopy={copier} />
      <div className={`fiche-orientation${orientation ? "" : " fiche-orientation--none"}`}>
        <div className="fiche-orientation__label">Orientation</div>
        {orientation ? (
          <>
            <div className="fiche-orientation__value">{orientation.name}</div>
            <div className="fiche-orientation__meta">
              {[orientation.type, orientation.date ? `le ${orientation.date}` : ""].filter(Boolean).join(" · ")}
              {orientation.maintainedAt && ` — maintien le ${orientation.maintainedAt}`}
            </div>
          </>
        ) : (
          <div className="fiche-orientation__value">Sans orientation</div>
        )}
      </div>
    </FicheHeader>
  );
};

export default ApercuAllocataire;
